/**
 * video-editor/timeline.js — модель монтажной ленты. Чистая арифметика: ни
 * DOM, ни WebCodecs, ни канвы. Поэтому её можно прогнать в node и проверить
 * то, что глазами на движущемся видео не проверить вовсе.
 *
 * Что такое лента здесь: упорядоченный ряд КЛИПОВ (видео и фото) на главной
 * дорожке, стоящих встык или с переходом, и поверх них — независимые НАДПИСИ
 * и ЗВУКОВЫЕ ДОРОЖКИ (музыка, голос) со своими окнами времени. Всё живёт в
 * одном времени вывода, и смысл модуля — переводить «время вывода» в «какой
 * клип, какая его секунда, с какой громкостью».
 *
 * ⚠️⚠️ ОДНА АРИФМЕТИКА НА ПРЕВЬЮ И ФАЙЛ. До 30.09.2026 экспорт шёл через
 * `VideoMergeWebCodecs` со СВОЕЙ арифметикой обрезки и скорости, а модель вела
 * только превью: мутацией 25.08 доказано, что две формулы могли разойтись
 * молча (убрал `trimStart` из модели — node-тесты покраснели, файл остался
 * «верным»). Теперь движок получает от модели ВСЁ, чем живёт лента:
 *   • `layout` — начала клипов, их длины и перекрытия переходов
 *     (opts.layout движка);
 *   • `frameAt(t)` — какой клип и какая секунда источника; экспорт спрашивает
 *     кадр ровно на эту секунду (hooks.compose → frameOf), превью ставит туда
 *     <video>;
 *   • `clipEnv(i)` / `itemEnv(i)` — огибающие громкости: по ним рендерит звук
 *     движок и по ним же выставляет громкость превью.
 * Сторож расхождения по-прежнему — §«превью совпадает с файлом» в
 * `tests/playwright/video-editor-e2e.mjs`.
 *
 * ⚠️ Обрезка и скорость взаимодействуют, и порядок важен. Длительность клипа на
 * ленте = (trimEnd − trimStart) / speed. Время ИСТОЧНИКА для момента вывода t
 * = trimStart + (t − clipStart) × speed.
 *
 * ⚠️ Переход — перекрытие соседей, а не пауза: клип i+1 начинается на ov раньше
 * конца клипа i. Перекрытие не больше половины более короткого соседа, поэтому
 * в любой момент видны НЕ БОЛЕЕ ДВУХ клипов (два перехода подряд на коротком
 * клипе иначе дали бы три слоя, а композитор смешивает два).
 *
 * Экспортируется как `window.VideoEditorTimeline` (браузер) и через
 * `module.exports` (node-тесты).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.VideoEditorTimeline = api;
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var EPS = 1e-6;
    /* Переходы, которые рисует VideoEditorCompose — тот же код у превью и у
       файла (hooks.compose движка), поэтому список здесь, а не у движка:
       предложить в интерфейсе то, что в файле станет простым наплывом,
       значит соврать. */
    var TRANSITIONS = ['fade', 'fadeblack', 'fadewhite', 'wipeleft', 'wiperight', 'wipeup', 'wipedown',
        'slideleft', 'slideright', 'slideup', 'slidedown', 'circleopen', 'circleclose', 'zoomin', 'zoomout', 'blur'];
    var TX_MIN = 0.1, TX_MAX = 3;
    var FITS = { contain: 1, cover: 1, blur: 1 };
    /* Фильтры — ключи общего набора SlideshowFilters (тонировка одной строкой
       ctx.filter, пиксельный путь для Safari ≤ 17 — CanvasCssFilter). */
    var FILTERS = { none: 1, warm: 1, cool: 1, vivid: 1, soft: 1, sepia: 1, vintage: 1, bw: 1, noir: 1 };
    var HEX = /^#[0-9a-f]{6}$/i;
    function hexOr(v, dflt) { return (typeof v === 'string' && HEX.test(v)) ? v.toLowerCase() : dflt; }
    /** Кадр без движения по источнику: фото и титульный кадр (цвет). */
    function isStill(c) { return !!c && (c.kind === 'image' || c.kind === 'color'); }

    function num(v, dflt) {
        var n = typeof v === 'number' ? v : parseFloat(v);
        return isFinite(n) ? n : dflt;
    }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    /* ── клип ────────────────────────────────────────────────────────────── */

    /**
     * Нормализует запись клипа к тому, на что можно опираться.
     * Кладём умолчания ЗДЕСЬ, а не в вызывающем коде: половина дефектов ленты —
     * это `undefined`, доехавший до арифметики и превративший время в NaN.
     */
    function normClip(raw, index) {
        var kind = raw && (raw.kind === 'image' || raw.kind === 'color') ? raw.kind : 'video';
        var still = kind !== 'video';
        var src = num(raw && raw.duration, 0);
        if (src < 0) src = 0;
        var ts = clamp(num(raw && raw.trimStart, 0), 0, src);
        var te = clamp(num(raw && raw.trimEnd, src), 0, src);
        if (te < ts) { var t = ts; ts = te; te = t; }
        var speed = num(raw && raw.speed, 1);
        if (!(speed > 0)) speed = 1;
        // Фото и титульный кадр живут столько, сколько им дали: скорость им
        // не нужна, а «источник» — просто длина.
        if (still) speed = 1;
        var tx = normTx(raw && raw.tx);
        // Доля хода движения фото, которую играет ЭТОТ клип: после разреза
        // вторая половина продолжает приближение, а не начинает его заново.
        var ms = raw && raw.motionSpan;
        var m0 = clamp(num(ms && ms[0], 0), 0, 1), m1 = clamp(num(ms && ms[1], 1), 0, 1);
        if (m1 < m0) { var mt = m0; m0 = m1; m1 = mt; }
        return {
            id: (raw && raw.id != null) ? raw.id : 'c' + index,
            kind: kind,
            mediaId: raw && raw.mediaId != null ? raw.mediaId : null,
            file: raw && raw.file,
            name: (raw && raw.name) || '',
            duration: src,
            trimStart: ts,
            trimEnd: te,
            speed: speed,
            volume: clamp(num(raw && raw.volume, 1), 0, 4),
            mute: !!(raw && raw.mute),
            // Появление и исчезновение клипа: картинка — из чёрного и в чёрный,
            // звук — нарастание и затухание. Сторона с переходом их не знает:
            // там клип уходит в соседа.
            fadeIn: clamp(num(raw && raw.fadeIn, 0), 0, 10),
            fadeOut: clamp(num(raw && raw.fadeOut, 0), 0, 10),
            rotation: ((num(raw && raw.rotation, 0) % 360) + 360) % 360,
            flipH: !!(raw && raw.flipH),
            flipV: !!(raw && raw.flipV),
            fit: FITS[raw && raw.fit] ? raw.fit : 'contain',
            // Цвет полей у «целиком» (у размытого фона и «заполнить» полей нет).
            bgColor: hexOr(raw && raw.bgColor, '#000000'),
            // Масштаб картинки в кадре поверх вписывания: 1 — как вписано,
            // 2 — вдвое крупнее (приближение детали); сдвиг — panX/panY.
            zoom: clamp(num(raw && raw.zoom, 1), 1, 4),
            // Кадрирование: какая часть лишнего попадает в кадр,
            // −1 — левый (верхний) край, 0 — середина, 1 — правый (нижний).
            panX: clamp(num(raw && raw.panX, 0), -1, 1),
            panY: clamp(num(raw && raw.panY, 0), -1, 1),
            // Движение фото: медленное приближение или отдаление за время показа.
            motion: (kind === 'image' && (raw && (raw.motion === 'in' || raw.motion === 'out'))) ? raw.motion : 'none',
            motionSpan: [m0, m1],
            // Цвет клипа: фильтр из общего набора и множители CSS-фильтра, 1 — как в исходнике.
            // Тонировка — ключ общего набора SlideshowFilters (не ctx.filter).
            tone: FILTERS[raw && raw.tone] ? raw.tone : 'none',
            bright: clamp(num(raw && raw.bright, 1), 0.3, 2),
            contrast: clamp(num(raw && raw.contrast, 1), 0.3, 2),
            sat: clamp(num(raw && raw.sat, 1), 0, 2),
            // Титульный кадр: заливка сверху вниз от color к color2.
            color: hexOr(raw && raw.color, '#1e293b'),
            color2: hexOr(raw && raw.color2, '#0f172a'),
            tx: tx,
            width: num(raw && raw.width, 0),
            height: num(raw && raw.height, 0),
            fps: num(raw && raw.fps, 0),
            hasAudio: still ? false : (raw ? raw.hasAudio !== false : true),
            reversed: !still && !!(raw && raw.reverseOf),
        };
    }

    function normTx(raw) {
        if (!raw || !raw.type || raw.type === 'none') return null;
        var type = TRANSITIONS.indexOf(raw.type) >= 0 ? raw.type : 'fade';
        return { type: type, dur: clamp(num(raw.dur, 0.6), TX_MIN, TX_MAX) };
    }

    /** Длительность клипа НА ЛЕНТЕ (с учётом обрезки и скорости). */
    function clipOutDuration(c) {
        var span = c.trimEnd - c.trimStart;
        if (!(span > 0)) return 0;
        return span / c.speed;
    }

    /* ── надпись ─────────────────────────────────────────────────────────── */

    function normText(raw, index) {
        var t = normTextBase(raw, index);
        t.ease = raw && raw.ease === 'linear' ? 'linear' : 'smooth';
        t.keys = normKeys(raw && raw.keys, 'text', { x: t.x, y: t.y, s: t.sizePct });
        return t;
    }
    function normTextBase(raw, index) {
        var start = Math.max(0, num(raw && raw.start, 0));
        var end = Math.max(start, num(raw && raw.end, start + 3));
        return {
            id: (raw && raw.id != null) ? raw.id : 't' + index,
            text: String((raw && raw.text) != null ? raw.text : ''),
            start: start,
            end: end,
            // Доля ширины/высоты кадра, а не пиксели: лента не знает разрешения
            // вывода, а вывод бывает и 720p, и 4K. Пиксели здесь означали бы,
            // что надпись съезжает при смене разрешения — тихо и всегда.
            x: clamp(num(raw && raw.x, 0.5), 0, 1),
            y: clamp(num(raw && raw.y, 0.85), 0, 1),
            sizePct: clamp(num(raw && raw.sizePct, 6), 1, 40),
            color: hexOr(raw && raw.color, '#ffffff'),
            // Подложка: обводка, плашка, свечение или ничего; её цвет — bgColor.
            bg: TEXT_BGS[raw && raw.bg] ? raw.bg : 'shadow',
            bgColor: hexOr(raw && raw.bgColor, '#000000'),
            align: ALIGNS[raw && raw.align] ? raw.align : 'center',
            fadeSec: clamp(num(raw && raw.fadeSec, 0.25), 0, 3),
            font: FONTS[raw && raw.font] ? raw.font : 'sans',
            anim: ANIMS[raw && raw.anim] ? raw.anim : 'fade',
        };
    }
    var FONTS = { sans: 1, narrow: 1, serif: 1, hand: 1, display: 1, script: 1 };
    var ANIMS = { fade: 1, slide: 1, pop: 1, type: 1, none: 1 };
    var TEXT_BGS = { shadow: 1, box: 1, glow: 1, none: 1 };
    var ALIGNS = { left: 1, center: 1, right: 1 };
    /* Длительность въезда/выезда анимированной надписи — не больше половины
       её окна, иначе у короткой надписи въезд съел бы выезд. */
    var ANIM_SEC = 0.45;

    /* ── звуковая дорожка ────────────────────────────────────────────────── */

    function normAudio(raw, index) {
        var src = Math.max(0, num(raw && raw.duration, 0));
        var ts = clamp(num(raw && raw.trimStart, 0), 0, src);
        var te = clamp(num(raw && raw.trimEnd, src), 0, src);
        if (te < ts) { var t = ts; ts = te; te = t; }
        return {
            id: (raw && raw.id != null) ? raw.id : 'a' + index,
            mediaId: raw && raw.mediaId != null ? raw.mediaId : null,
            name: (raw && raw.name) || '',
            duration: src,
            start: Math.max(0, num(raw && raw.start, 0)),
            trimStart: ts,
            trimEnd: te,
            volume: clamp(num(raw && raw.volume, 1), 0, 4),
            fadeIn: clamp(num(raw && raw.fadeIn, 0), 0, 30),
            fadeOut: clamp(num(raw && raw.fadeOut, 0), 0, 30),
            // «Повторять до конца видео»: дорожка тянется до конца ленты и
            // играет выбранный отрезок по кругу.
            loop: !!(raw && raw.loop),
            mute: !!(raw && raw.mute),
            // Тише, пока звучит видео или записанный голос (влог: музыка под речью).
            duck: !!(raw && raw.duck),
            // Дубль с микрофона: под ним «приглушаемая» музыка тоже уходит вниз.
            voice: !!(raw && raw.voice),
        };
    }
    /* ── ключевые кадры (движение наложения и титра) ─────────────────────
       Ключ — момент ленты (абсолютное время, как у точек скрытия) и значения
       в нём; между ключами значения идут плавно (или ровно — `ease`), до
       первого и после последнего стоят на месте. Абсолютное время выбрано
       ради общей привязки к кадру: reanchor и «дублировать» уже сдвигают
       `keys` любого элемента вместе с ним. */
    var KEY_FIELDS = {
        overlay: { x: [0, 1], y: [0, 1], w: [0.03, 1], o: [0, 1] },
        text: { x: [0, 1], y: [0, 1], s: [1, 40] },
    };
    /** Ключ, стоящий ближе этого к моменту, — «ключ в этом кадре». */
    var KEY_SNAP = 0.02;
    function normKeys(raw, kind, base) {
        var spec = KEY_FIELDS[kind], out = [];
        (Array.isArray(raw) ? raw : []).forEach(function (k) {
            var t = num(k && k.t, NaN);
            if (!isFinite(t)) return;
            var o = { t: Math.max(0, t) };
            Object.keys(spec).forEach(function (f) { o[f] = clamp(num(k[f], base[f]), spec[f][0], spec[f][1]); });
            out.push(o);
        });
        out.sort(function (a, b) { return a.t - b.t; });
        var d = [];
        out.forEach(function (k) {
            if (d.length && Math.abs(k.t - d[d.length - 1].t) < 1e-3) d[d.length - 1] = k; else d.push(k);
        });
        return d;
    }
    /** Значения ключей в момент t (null — ключей нет, элемент стоит как стоит). */
    function keyedAt(keys, t, ease) {
        if (!keys || !keys.length) return null;
        if (keys.length === 1 || t <= keys[0].t) return keys[0];
        var last = keys[keys.length - 1];
        if (t >= last.t) return last;
        for (var i = 0; i < keys.length - 1; i++) {
            var a = keys[i], b = keys[i + 1];
            if (t > b.t) continue;
            var p = (b.t - a.t) > 1e-9 ? (t - a.t) / (b.t - a.t) : 1;
            if (ease !== 'linear') p = p * p * (3 - 2 * p);
            var o = { t: t };
            Object.keys(a).forEach(function (f) { if (f !== 't') o[f] = a[f] + (b[f] - a[f]) * p; });
            return o;
        }
        return last;
    }

    /* ── наложение (логотип, стикер, картинка или ВИДЕО поверх видео) ───── */

    var SHAPES = { rect: 1, round: 1, circle: 1 };
    var BORDERS = { none: 1, white: 1, black: 1 };
    /**
     * Хромакей: цвет фона, который становится прозрачным (зелёный или синий
     * экран), близость к нему (sim), мягкость края (smooth) и подавление
     * отсвета фона на краях (spill) — доли расстояния в плоскости цветности.
     */
    function normChroma(raw) {
        if (!raw || !raw.color) return null;
        return {
            color: hexOr(raw.color, '#00ff00'),
            sim: clamp(num(raw.sim, 0.3), 0.02, 0.9),
            smooth: clamp(num(raw.smooth, 0.08), 0, 0.5),
            spill: clamp(num(raw.spill, 0.1), 0, 0.5),
        };
    }

    function normOverlay(raw, index) {
        var kind = raw && raw.kind === 'video' ? 'video' : 'image';
        var start = Math.max(0, num(raw && raw.start, 0));
        var o = {
            id: (raw && raw.id != null) ? raw.id : 'o' + index,
            kind: kind,
            mediaId: raw && raw.mediaId != null ? raw.mediaId : null,
            name: (raw && raw.name) || '',
            start: start,
            end: Math.max(start + 0.1, num(raw && raw.end, start + 5)),
            // Центр картинки и её ширина — доли кадра, как у титра: вывод
            // бывает и 720p, и 4K, а положение обязано остаться тем же.
            x: clamp(num(raw && raw.x, 0.82), 0, 1),
            y: clamp(num(raw && raw.y, 0.18), 0, 1),
            w: clamp(num(raw && raw.w, 0.25), 0.03, 1),
            opacity: clamp(num(raw && raw.opacity, 1), 0.05, 1),
            anim: (raw && (raw.anim === 'pop' || raw.anim === 'none')) ? raw.anim : 'fade',
            // Форма: прямоугольник, скруглённые углы или круг (кружок с лицом).
            shape: SHAPES[raw && raw.shape] ? raw.shape : 'rect',
            border: BORDERS[raw && raw.border] ? raw.border : 'none',
            chroma: normChroma(raw && raw.chroma),
            ease: raw && raw.ease === 'linear' ? 'linear' : 'smooth',
        };
        o.keys = normKeys(raw && raw.keys, 'overlay', { x: o.x, y: o.y, w: o.w, o: o.opacity });
        if (kind === 'video') {
            /* Видео поверх видео живёт отрезком СВОЕГО файла, как клип: длина
               на ленте — это длина отрезка (скорость 1), а не произвольный
               конец. Иначе «растянуть» наложение значило бы показать кадры,
               которых в выбранном отрезке нет. */
            var src = Math.max(0, num(raw && raw.duration, 0));
            var ts = clamp(num(raw && raw.trimStart, 0), 0, src);
            var te = clamp(num(raw && raw.trimEnd, src), 0, src);
            if (te < ts) { var tt = ts; ts = te; te = tt; }
            o.duration = src;
            o.trimStart = ts;
            o.trimEnd = te;
            o.end = start + Math.max(0.1, te - ts);
            o.volume = clamp(num(raw && raw.volume, 1), 0, 4);
            o.mute = !!(raw && raw.mute);
            o.hasAudio = !raw || raw.hasAudio !== false;
        }
        return o;
    }

    var DUCK_GAIN = 0.3;      // во сколько раз тише под звуком видео (≈ −10 дБ)
    var DUCK_RAMP = 0.35;     // с — съезд и возврат громкости

    /* ── огибающая громкости ─────────────────────────────────────────────── */

    /** Громкость по ломаной [[t, g], …] — та же интерполяция, что у движка. */
    function gainAt(bps, t) {
        if (!bps || !bps.length) return 0;
        if (t <= bps[0][0]) return bps[0][1];
        for (var i = 1; i < bps.length; i++) {
            if (t <= bps[i][0]) {
                var t0 = bps[i - 1][0], t1 = bps[i][0], v0 = bps[i - 1][1], v1 = bps[i][1];
                return t1 <= t0 ? v1 : v0 + (v1 - v0) * ((t - t0) / (t1 - t0));
            }
        }
        return bps[bps.length - 1][1];
    }

    /** Ломаная «нарастание — полка — спад» на отрезке [s, e]. */
    function rampEnv(s, e, vol, inSec, outSec) {
        var len = Math.max(0, e - s);
        var fi = Math.max(0, inSec || 0), fo = Math.max(0, outSec || 0);
        if (fi + fo > len && fi + fo > 0) { var k = len / (fi + fo); fi *= k; fo *= k; }
        var inEnd = s + fi, outStart = e - fo;
        return [[s, fi > 0 ? 0 : vol], [inEnd, vol], [outStart, vol], [e, fo > 0 ? 0 : vol]];
    }

    /* ── лента ───────────────────────────────────────────────────────────── */

    /**
     * @param {Array} clipsRaw  клипы в порядке показа
     * @param {Array} textsRaw  надписи (порядок не важен — окна независимы)
     * @param {Object} [extra] { audio: [звуковые дорожки] }
     */
    function build(clipsRaw, textsRaw, extra) {
        var clips = (clipsRaw || []).map(normClip);
        var texts = (textsRaw || []).map(normText);
        var audio = ((extra && extra.audio) || []).map(normAudio);
        var overlays = ((extra && extra.overlays) || []).map(normOverlay);
        var n = clips.length;

        var effDur = clips.map(clipOutDuration);
        // Перекрытия переходов: клип i уходит в i+1. Пустой сосед (обрезан в
        // точку) перехода не держит — иначе он «съел» бы кадры соседа.
        var overlaps = [];
        for (var i = 0; i < n - 1; i++) {
            var c = clips[i], ov = 0;
            if (c.tx && effDur[i] > 0 && effDur[i + 1] > 0) {
                ov = Math.min(c.tx.dur, 0.5 * Math.min(effDur[i], effDur[i + 1]));
                if (ov < TX_MIN - EPS) ov = 0;
            }
            overlaps.push(ov);
        }
        var starts = [], acc = 0;
        for (var k = 0; k < n; k++) {
            starts.push(acc);
            acc += effDur[k] - (k < n - 1 ? overlaps[k] : 0);
        }
        // Конец ленты — конец последнего клипа (у последнего перекрытия нет).
        var total = n ? Math.max(0, starts[n - 1] + effDur[n - 1]) : 0;
        var lastFull = -1;
        for (var q = n - 1; q >= 0; q--) if (effDur[q] > 0) { lastFull = q; break; }

        function entry(k2, t) {
            var cl = clips[k2], d = effDur[k2];
            var local = clamp(t - starts[k2], 0, d);
            var srcSec = isStill(cl) ? 0 : clamp(cl.trimStart + local * cl.speed, cl.trimStart, cl.trimEnd);
            return { index: k2, clip: cl, localSec: local, sourceSec: srcSec };
        }

        /**
         * Момент вывода → что показывать.
         * @returns {null|{a, b, prog, type}} a — клип (на переходе — уходящий),
         *   b — входящий клип на переходе или null, prog 0→1 по переходу.
         *   null — за пределами ленты (чёрный кадр).
         */
        function frameAt(t) {
            var tt = num(t, 0);
            if (!(total > 0) || tt < -EPS || tt > total + EPS) return null;
            var act = [];
            for (var k2 = 0; k2 < n; k2++) {
                var d = effDur[k2];
                if (d <= 0) continue;
                var s = starts[k2];
                // Правая граница включается ТОЛЬКО у последнего клипа: иначе на
                // стыке подходят оба, и кадр стыка берётся то из одного, то из
                // другого в зависимости от порядка обхода.
                var within = (k2 === lastFull) ? (tt >= s - EPS && tt <= s + d + EPS)
                                               : (tt >= s - EPS && tt < s + d - EPS);
                if (within) act.push(k2);
            }
            if (!act.length) return null;
            if (act.length === 1) return { a: entry(act[0], tt), b: null, prog: 0, type: null };
            var ka = act[act.length - 2], kb = act[act.length - 1];
            var ovab = (kb === ka + 1) ? overlaps[ka] : 0;
            if (!(ovab > 0)) return { a: entry(kb, tt), b: null, prog: 0, type: null };
            var prog = clamp((tt - starts[kb]) / ovab, 0, 1);
            return { a: entry(ka, tt), b: entry(kb, tt), prog: prog, type: clips[ka].tx ? clips[ka].tx.type : 'fade' };
        }

        /**
         * Ломаная «появление — полка — исчезновение» клипа i с высотой vol.
         * withTx — стороны с переходом тоже спадают (звук переходит крест-
         * накрест); без него сторона с переходом не спадает вовсе: картинку
         * там смешивает переход, а не уход в чёрный.
         */
        function fadeShape(i2, vol, withTx) {
            var cl = clips[i2], s = starts[i2], d = effDur[i2];
            var inOv = i2 > 0 ? overlaps[i2 - 1] : 0;
            var outOv = i2 < n - 1 ? overlaps[i2] : 0;
            var inEnd = s, outStart = s + d;
            if (inOv > 0) { if (withTx) inEnd = s + inOv; }
            else if (cl.fadeIn > 0) inEnd = s + Math.min(cl.fadeIn, d);
            if (outOv > 0) { if (withTx) outStart = s + d - outOv; }
            else if (cl.fadeOut > 0) outStart = s + Math.max(0, d - cl.fadeOut);
            if (inEnd > outStart) inEnd = outStart;   // короткий клип: затухания встречаются
            return [[s, inEnd > s ? 0 : vol], [inEnd, vol], [outStart, vol], [s + d, outStart < s + d ? 0 : vol]];
        }
        /** Громкость звука клипа i: затухания, стыки переходов, «без звука». */
        function clipEnv(i2) {
            var cl = clips[i2];
            return fadeShape(i2, (cl.mute || !cl.hasAudio) ? 0 : cl.volume, true);
        }
        /**
         * Яркость картинки клипа i в момент t: 1 — как есть, 0 — чёрный кадр.
         * Те же затухания, что у звука клипа, — появление из чёрного звучит
         * нарастанием, а не отдельной настройкой, которую надо свести руками.
         */
        function clipFadeAt(i2, t) {
            if (i2 < 0 || i2 >= n) return 1;
            var cl = clips[i2];
            if (!(cl.fadeIn > 0) && !(cl.fadeOut > 0)) return 1;
            return clamp(gainAt(fadeShape(i2, 1, false), t), 0, 1);
        }

        /** Место звуковой дорожки на ленте. Конец — не дальше конца видео. */
        function itemSpan(j) {
            var a = audio[j];
            var len = a.loop ? Math.max(0, total - a.start) : Math.max(0, a.trimEnd - a.trimStart);
            var end = a.start + len;
            if (total > 0) end = Math.min(end, total);
            return { start: a.start, end: Math.max(a.start, end), len: Math.max(0, end - a.start) };
        }
        /**
         * Отрезки ленты, где звучит речь: клип со звуком (не заглушён) и
         * записанный голос. `exceptJ` — сама дорожка: под собой она не глохнет.
         */
        function soundSpans(exceptJ) {
            var raw = [];
            for (var q = 0; q < n; q++) {
                var cq = clips[q];
                if (!(effDur[q] > 0) || !cq.hasAudio || cq.mute || !(cq.volume > 0)) continue;
                raw.push([starts[q], starts[q] + effDur[q]]);
            }
            for (var v = 0; v < audio.length; v++) {
                var av = audio[v];
                if (v === exceptJ || !av.voice || av.mute || !(av.volume > 0)) continue;
                var sv = itemSpan(v);
                if (sv.len > 0) raw.push([sv.start, sv.end]);
            }
            // Видео поверх видео со звуком (ведущий в кружке) — тоже речь.
            for (var w = 0; w < overlays.length; w++) {
                var ow = overlays[w];
                if (ow.kind !== 'video' || !ow.hasAudio || ow.mute || !(ow.volume > 0)) continue;
                raw.push([ow.start, ow.end]);
            }
            raw.sort(function (x, y) { return x[0] - y[0]; });
            var out = [];
            raw.forEach(function (r) {
                if (out.length && r[0] <= out[out.length - 1][1] + 1e-6) out[out.length - 1][1] = Math.max(out[out.length - 1][1], r[1]);
                else out.push([r[0], r[1]]);
            });
            return out;
        }
        /** Множитель приглушения в момент t: 1 — нет видео со звуком, DUCK_GAIN — есть. */
        function duckAt(spans, t) {
            var g = 1;
            for (var q = 0; q < spans.length; q++) {
                var a0 = spans[q][0], a1 = spans[q][1], f = 0;
                if (t >= a0 && t <= a1) f = 1;
                else if (t > a0 - DUCK_RAMP && t < a0) f = (t - (a0 - DUCK_RAMP)) / DUCK_RAMP;
                else if (t > a1 && t < a1 + DUCK_RAMP) f = 1 - (t - a1) / DUCK_RAMP;
                if (f > 0) g = Math.min(g, 1 - (1 - DUCK_GAIN) * f);
            }
            return g;
        }
        /**
         * Громкость дорожки: затухание в начале и в конце ЗВУЧАЩЕГО отрезка, а у
         * «приглушать» — ещё и съезд под звук видео. Произведение двух ломаных
         * снова ломаная в узлах обеих — её и едят превью (gainAt) и движок
         * (linearRamp), одинаково.
         */
        function itemEnv(j) {
            var a = audio[j], sp = itemSpan(j);
            var base = rampEnv(sp.start, sp.end, a.mute ? 0 : a.volume, a.fadeIn, a.fadeOut);
            if (!a.duck) return base;
            var spans = soundSpans(j), ts = [];
            base.forEach(function (b) { ts.push(b[0]); });
            spans.forEach(function (s) { ts.push(s[0] - DUCK_RAMP, s[0], s[1], s[1] + DUCK_RAMP); });
            ts = ts.filter(function (x) { return x >= sp.start - 1e-9 && x <= sp.end + 1e-9; }).sort(function (x, y) { return x - y; });
            var out = [];
            for (var q = 0; q < ts.length; q++) {
                if (out.length && Math.abs(ts[q] - out[out.length - 1][0]) < 1e-6) continue;
                out.push([ts[q], gainAt(base, ts[q]) * duckAt(spans, ts[q])]);
            }
            return out.length >= 2 ? out : base;
        }
        /**
         * Звук видео-наложения j: нарастает и спадает вместе с картинкой
         * (появление наложения — 0.3 с), иначе оно вступало бы щелчком.
         */
        function ovFade(o) { return o.anim === 'none' ? 0 : Math.min(0.3, (o.end - o.start) / 3); }
        function ovEnv(j) {
            var o = overlays[j];
            if (!o || o.kind !== 'video') return null;
            var f = ovFade(o);
            return rampEnv(o.start, o.end, (o.mute || !o.hasAudio) ? 0 : o.volume, f, f);
        }
        /** Секунда источника видео-наложения j в момент t (null — не показывается). */
        function ovSourceAt(j, t) {
            var o = overlays[j];
            if (!o || o.kind !== 'video' || t < o.start - EPS || t > o.end + EPS) return null;
            return clamp(o.trimStart + (t - o.start), o.trimStart, o.trimEnd);
        }
        /** Секунда источника дорожки j в момент t (null — дорожка молчит). */
        function itemSourceAt(j, t) {
            var a = audio[j], sp = itemSpan(j);
            if (t < sp.start - EPS || t >= sp.end - EPS) return null;
            var seg = a.trimEnd - a.trimStart;
            if (!(seg > 0.01)) return null;
            var off = t - sp.start;
            if (a.loop) off = off % seg; else if (off >= seg) return null;
            return a.trimStart + off;
        }

        return {
            clips: clips,
            texts: texts,
            audio: audio,
            overlays: overlays,
            starts: starts,
            effDur: effDur,
            overlaps: overlaps,
            duration: total,
            /** Раскладка для движка (opts.layout) — те же числа, что у превью. */
            layout: { starts: starts.slice(), effDur: effDur.slice(), overlaps: overlaps.slice() },

            frameAt: frameAt,
            /** Старый вызов: клип, откуда брать кадр (на переходе — уходящий). */
            frameSource: function (t) { var f = frameAt(t); return f ? f.a : null; },

            /**
             * Надписи, видимые в момент t: прозрачность на въезде/выезде и ход
             * анимации (pin — въезд 0→1, pout — выезд 1→0, local — секунды от
             * появления). Одни и те же числа рисуют превью и файл.
             */
            textsAt: function (t) {
                var tt = num(t, 0), out = [];
                for (var j = 0; j < texts.length; j++) {
                    var x = texts[j];
                    if (tt < x.start - EPS || tt > x.end + EPS) continue;
                    if (!x.text) continue;
                    var span = x.end - x.start;
                    var fade = x.anim === 'none' ? 0 : (x.anim === 'fade' ? Math.min(x.fadeSec, span / 2) : Math.min(ANIM_SEC, span / 2));
                    var al = 1;
                    if (fade > 0) {
                        // Печать проявляет буквы сама — прозрачность ей только на выезде.
                        if (tt < x.start + fade && x.anim !== 'type') al = (tt - x.start) / fade;
                        else if (tt > x.end - fade) al = (x.end - tt) / fade;
                    }
                    var a2 = Math.min(ANIM_SEC, span / 2);
                    // Ключевые кадры: положение и размер в этот момент. Копия,
                    // а не правка записи ленты — модель нигде не меняется на месте.
                    var kv = keyedAt(x.keys, tt, x.ease);
                    if (kv) x = Object.assign({}, x, { x: kv.x, y: kv.y, sizePct: kv.s });
                    out.push({
                        item: x, alpha: clamp(al, 0, 1),
                        pin: a2 > 0 ? clamp((tt - x.start) / a2, 0, 1) : 1,
                        pout: a2 > 0 ? clamp((x.end - tt) / a2, 0, 1) : 1,
                        local: clamp(tt - x.start, 0, span), span: span,
                    });
                }
                return out;
            },

            /**
             * Наложения, видимые в момент t: прозрачность на въезде/выезде
             * (0.3 с, не больше трети окна) и масштаб «всплытия».
             */
            overlaysAt: function (t) {
                var tt = num(t, 0), out = [];
                for (var j = 0; j < overlays.length; j++) {
                    var o = overlays[j];
                    if (tt < o.start - EPS || tt > o.end + EPS) continue;
                    var span = o.end - o.start, f = o.anim === 'none' ? 0 : Math.min(0.3, span / 3);
                    var pin = f > 0 ? clamp((tt - o.start) / f, 0, 1) : 1, pout = f > 0 ? clamp((o.end - tt) / f, 0, 1) : 1;
                    var al = Math.min(pin, pout), sc = 1;
                    if (o.anim === 'pop' && pin < 1) sc = 0.6 + 0.4 * pin;
                    var kv = keyedAt(o.keys, tt, o.ease), shown = o;
                    if (kv) shown = Object.assign({}, o, { x: kv.x, y: kv.y, w: kv.w, opacity: kv.o });
                    out.push({ item: shown, index: j, alpha: clamp(al, 0, 1), scale: sc,
                        sourceSec: o.kind === 'video' ? ovSourceAt(j, tt) : null });
                }
                return out;
            },

            /** Границы клипа на ленте — для отрисовки полосы и попаданий мышью. */
            spanOf: function (index) {
                if (index < 0 || index >= n) return null;
                return { start: starts[index], end: starts[index] + effDur[index] };
            },

            clipEnv: clipEnv,
            clipGainAt: function (i2, t) { return gainAt(clipEnv(i2), t); },
            clipFadeAt: clipFadeAt,
            itemSpan: itemSpan,
            itemEnv: itemEnv,
            itemGainAt: function (j, t) { return gainAt(itemEnv(j), t); },
            itemSourceAt: itemSourceAt,
            ovEnv: ovEnv,
            ovGainAt: function (j, t) { var e = ovEnv(j); return e ? gainAt(e, t) : 0; },
            ovSourceAt: ovSourceAt,
            /** Где кончается всё, что есть на ленте (для ширины и линейки). */
            extent: function () {
                var e = total;
                for (var j = 0; j < audio.length; j++) {
                    var a = audio[j];
                    e = Math.max(e, a.start + Math.max(0, a.trimEnd - a.trimStart));
                }
                for (var m = 0; m < texts.length; m++) e = Math.max(e, texts[m].end);
                for (var q = 0; q < overlays.length; q++) e = Math.max(e, overlays[q].end);
                return e;
            },
        };
    }

    /* ── операции ленты ──────────────────────────────────────────────────── */

    /**
     * Переставить клип. Возвращает НОВЫЙ массив — модель нигде не правится на
     * месте, чтобы «отменить» оставалось одной подстановкой прежнего массива.
     */
    function move(clipsRaw, from, to) {
        var a = (clipsRaw || []).slice();
        if (from < 0 || from >= a.length) return a;
        to = clamp(to, 0, a.length - 1);
        var it = a.splice(from, 1)[0];
        a.splice(to, 0, it);
        return a;
    }

    /**
     * Разрезать клип в момент ВЫВОДА t. Половинки наследуют скорость и звук;
     * переход наружу уходит с ПРАВОЙ половиной (он на её краю), между
     * половинками стык встык.
     * Возвращает { clips, splitAt, reason } — splitAt — индекс ПЕРВОЙ половины,
     * или −1, если резать нечего: точка на самом краю, мимо ленты или на
     * переходе (reason 'transition').
     */
    function splitAt(clipsRaw, t, newId) {
        var tl = build(clipsRaw, []);
        var f = tl.frameAt(t);
        if (!f) return { clips: (clipsRaw || []).slice(), splitAt: -1, reason: 'outside' };
        if (f.b) return { clips: (clipsRaw || []).slice(), splitAt: -1, reason: 'transition' };
        var hit = f.a, c = hit.clip;
        var a = (clipsRaw || []).slice();
        var left, right;
        /* На месте реза стык встык: исчезновение левой половины и появление
           правой остались бы на нём провалом в чёрный и в тишину, которого в
           клипе не было (у звуковой дорожки то же самое обнуляется так же). */
        var seam = { fadeOut: 0 }, seamR = { fadeIn: 0 };
        if (isStill(c)) {
            var len = tl.effDur[hit.index], cutL = hit.localSec;
            if (cutL <= 0.05 || cutL >= len - 0.05) return { clips: a, splitAt: -1, reason: 'edge' };
            // Движение фото продолжается через рез: левая половина играет свою
            // долю хода, правая — остаток, без прыжка масштаба на стыке.
            var ms = c.motionSpan, mc = ms[0] + (ms[1] - ms[0]) * (cutL / len);
            left = Object.assign({}, a[hit.index], seam, { duration: cutL, trimStart: 0, trimEnd: cutL, tx: null, motionSpan: [ms[0], mc] });
            right = Object.assign({}, a[hit.index], seamR, { duration: len - cutL, trimStart: 0, trimEnd: len - cutL, id: newId || null, motionSpan: [mc, ms[1]] });
        } else {
            var cut = hit.sourceSec;
            // Рез вплотную к краю не создаёт клип, он создаёт пустоту.
            if (cut <= c.trimStart + 1e-3 || cut >= c.trimEnd - 1e-3) return { clips: a, splitAt: -1, reason: 'edge' };
            left = Object.assign({}, a[hit.index], seam, { trimStart: c.trimStart, trimEnd: cut, tx: null });
            right = Object.assign({}, a[hit.index], seamR, { trimStart: cut, trimEnd: c.trimEnd, id: newId || null });
        }
        a.splice(hit.index, 1, left, right);
        return { clips: a, splitAt: hit.index, reason: null };
    }

    /* ── привязка к кадру ─────────────────────────────────────────────────
       Титры, скрытие и звук стоят над КАДРОМ, а не над секундой ленты: титр
       «Бабушка» — под бабушкой, пиксели — на номере машины. До 01.10.2026
       они жили временем ленты, и обрезка первого клипа, удаление или
       перестановка молча уводили их с места — скрытие съезжало с номера,
       который человек уже закрыл (разбор кода, 30.09). Теперь правка ленты
       (обрезка, скорость, удаление, вставка, перестановка, переход)
       сначала запоминает, над каким кадром какого клипа стоит начало
       каждого элемента, а потом ставит его туда же в новой раскладке.
       Длина элемента не меняется. Так ведёт себя и «sync lock» монтажных
       программ: всё, что стоит после правки, едет вместе с ней. */

    /** Где на старой ленте стоит момент t: клип и кадр его источника. */
    function anchorAt(tl, t) {
        if (!(tl.duration > 0)) return { abs: t };
        // Начало ролика — это «с самого начала», а не «с первого кадра первого
        // клипа»: музыка и логотип с нуля остаются с нуля, когда перед первым
        // клипом встаёт заставка.
        if (t <= EPS) return { abs: 0, id0: tl.clips.length ? tl.clips[0].id : null };
        if (t >= tl.duration - EPS) return { tail: t - tl.duration };
        var f = tl.frameAt(Math.max(0, t));
        if (!f) return { abs: t };
        var en = f.b || f.a;
        var after = [];
        for (var i = en.index + 1; i < tl.clips.length; i++) after.push(tl.clips[i].id);
        return { id: en.clip.id, still: isStill(en.clip), local: en.localSec, src: en.sourceSec, after: after };
    }
    /**
     * Конец элемента. Конец ровно на конце клипа — это «до конца клипа» (титр
     * заставки): после правки он кончается там же, где кончается клип, а не
     * там, где начинается его сосед.
     */
    function anchorEndAt(tl, t) {
        for (var k = 0; k < tl.clips.length; k++) {
            var e = tl.starts[k] + tl.effDur[k];
            if (tl.effDur[k] > 0 && Math.abs(t - e) < 0.02) return { id: tl.clips[k].id, edge: 'end' };
        }
        return anchorAt(tl, t);
    }
    function resolveEnd(tl, a) {
        if (a && a.edge === 'end') {
            for (var i = 0; i < tl.clips.length; i++) if (tl.clips[i].id === a.id) return { t: tl.starts[i] + tl.effDur[i], clamped: false };
            return { t: tl.duration, clamped: true };
        }
        return resolveAnchor(tl, a);
    }
    /** Тот же кадр на новой ленте; clamped — кадра больше нет (срезан). */
    function resolveAnchor(tl, a) {
        if (a.abs != null) return { t: a.abs, clamped: false };
        if (a.tail != null) return { t: tl.duration + a.tail, clamped: false };
        for (var i = 0; i < tl.clips.length; i++) {
            var c = tl.clips[i];
            if (c.id !== a.id) continue;
            var d = tl.effDur[i];
            var local = a.still ? a.local : (a.src - c.trimStart) / c.speed;
            return { t: tl.starts[i] + clamp(local, 0, d), clamped: local < -EPS || local > d + EPS };
        }
        // Клип удалён: элемент встаёт туда, где начинался следующий уцелевший.
        for (var j = 0; j < (a.after || []).length; j++) {
            for (var k = 0; k < tl.clips.length; k++) if (tl.clips[k].id === a.after[j]) return { t: tl.starts[k], clamped: true };
        }
        return { t: tl.duration, clamped: true };
    }
    /**
     * Запомнить места элементов перед правкой ленты.
     * @param {Array} clipsRaw  клипы ДО правки
     * @param {Object} items    { texts:[], audio:[], regions:[], overlays:[] } — элементы со start/end
     */
    function anchorsOf(clipsRaw, items) {
        var tl = build(clipsRaw, []);
        var out = [];
        ['texts', 'audio', 'regions', 'overlays'].forEach(function (key) {
            (items[key] || []).forEach(function (it) {
                var s = num(it.start, 0);
                var len = key === 'audio' ? null : Math.max(0, num(it.end, s) - s);
                // Элемент до самого конца ролика (логотип на всё видео) и после
                // правки тянется до конца, а не обрывается раньше.
                // Видео поверх видео длиной в свой отрезок: длину не меняет ни
                // «до конца ролика», ни «кончается с клипом» — только место.
                var fixedLen = key === 'overlays' && it.kind === 'video';
                var toEnd = !fixedLen && len != null && tl.duration > 0 && s + len >= tl.duration - 0.05;
                out.push({ key: key, id: it.id, start: s, len: len, toEnd: toEnd, fixedLen: fixedLen, a0: anchorAt(tl, s),
                    a1: len != null ? anchorEndAt(tl, s + len) : null });
            });
        });
        return out;
    }
    /**
     * Поставить элементы на их кадры в новой раскладке. Меняет элементы на
     * месте (это новые объекты проекта, «отменить» держит прежний снимок) и
     * отдаёт сдвиги по id — их ключевые точки (скрытие) едут на тот же сдвиг.
     */
    function reanchor(clipsRaw, items, anchors) {
        var tl = build(clipsRaw, []);
        var moved = {};
        anchors.forEach(function (an) {
            var list = items[an.key] || [], it = null;
            for (var i = 0; i < list.length; i++) if (list[i].id === an.id) { it = list[i]; break; }
            if (!it) return;
            var r0 = resolveAnchor(tl, an.a0), ns = r0.t;
            // Начало срезано, а конец уцелел — ставим по концу: скрытие
            // остаётся на той части номера, что ещё в кадре.
            if (r0.clamped && an.a1) {
                var r1 = resolveEnd(tl, an.a1);
                if (!r1.clamped) ns = r1.t - an.len;
            }
            ns = Math.max(0, ns);
            var delta = ns - an.start;
            /* Конец. Элемент внутри ОДНОГО клипа ставит оба края по кадрам:
               ускорил клип — титр над ним стал короче вместе с кадрами, укоротил
               заставку — её заголовок кончается вместе с ней. Элемент над
               несколькими клипами сохраняет длину (перестановка иначе растянула
               бы его через чужой клип). */
            var ne = null;
            if (an.len != null) {
                var c0 = an.a0 ? (an.a0.id != null ? an.a0.id : an.a0.id0) : null;
                if (an.fixedLen) ne = ns + an.len;
                else if (an.toEnd && tl.duration > ns + 0.1) ne = tl.duration;
                else if (c0 != null && an.a1 && an.a1.id === c0) {
                    var re = resolveEnd(tl, an.a1);
                    ne = (!re.clamped && re.t > ns + 0.05) ? re.t : ns + an.len;
                } else ne = ns + an.len;
            }
            if (Math.abs(delta) < 1e-6 && (ne == null || Math.abs(ne - (an.start + an.len)) < 1e-6)) return;
            it.start = ns;
            if (ne != null) it.end = ne;
            if (it.keys && Math.abs(delta) >= 1e-6) it.keys.forEach(function (k) { k.t = num(k.t, 0) + delta; });
            moved[an.id] = delta;
        });
        return moved;
    }

    /* ── обратный ход ──────────────────────────────────────────────────────
       Реверс — перевёрнутая копия ОТРЕЗКА клипа (движок /reverse-video), на
       которую клип просто переезжает. Секунда r копии — это секунда
       end − r исходника, поэтому запоминается КОНЕЦ исходного отрезка, а не
       его обрезка: после разреза и подрезки перевёрнутого клипа «обычный ход»
       всё равно возвращает каждую половину на её собственные кадры. */

    /** Клип на перевёрнутой копии rev = { mediaId, duration } его отрезка. */
    function reversedClip(raw, rev) {
        var d = Math.max(0.05, num(rev && rev.duration, 0));
        return Object.assign({}, raw, {
            mediaId: rev.mediaId, duration: d, trimStart: 0, trimEnd: d,
            reverseOf: { mediaId: raw.mediaId, end: num(raw.trimEnd, 0), duration: num(raw.duration, 0), name: raw.name || '' },
        });
    }
    /** Обратно на исходник: те же кадры, что сейчас у перевёрнутого клипа. */
    function forwardClip(raw) {
        var rv = raw && raw.reverseOf;
        if (!rv) return raw;
        var src = Math.max(0, num(rv.duration, 0));
        var ts = clamp(rv.end - num(raw.trimEnd, 0), 0, src), te = clamp(rv.end - num(raw.trimStart, 0), 0, src);
        var out = Object.assign({}, raw, { mediaId: rv.mediaId, duration: src, trimStart: Math.min(ts, te), trimEnd: Math.max(ts, te) });
        delete out.reverseOf;
        return out;
    }

    /* ── правка ключевых кадров ────────────────────────────────────────────
       Правила одни для мыши в превью, ползунков и кнопок, поэтому живут
       здесь, рядом с интерполяцией, а не в обработчиках страницы. */

    function baseValues(kind, it) {
        return kind === 'overlay' ? { x: it.x, y: it.y, w: it.w, o: it.opacity } : { x: it.x, y: it.y, s: it.sizePct };
    }
    /** Значения элемента в момент t с учётом ключей: {x,y,w,o} у наложения, {x,y,s} у титра. */
    function valuesAt(kind, raw, t) {
        var it = kind === 'overlay' ? normOverlay(raw, 0) : normText(raw, 0);
        var base = baseValues(kind, it);
        var kv = keyedAt(it.keys, t, it.ease);
        if (!kv) return base;
        var out = {};
        Object.keys(base).forEach(function (f) { out[f] = kv[f]; });
        return out;
    }
    /** Ключ в этом кадре (не дальше KEY_SNAP от момента) или null. */
    function keyNear(raw, t) {
        var ks = (raw && raw.keys) || [], best = null, bd = KEY_SNAP + 1e-9;
        for (var i = 0; i < ks.length; i++) {
            var d = Math.abs(num(ks[i].t, -1e9) - t);
            if (d <= bd) { bd = d; best = ks[i]; }
        }
        return best;
    }
    function applyStatic(kind, raw, v) {
        if (v.x != null) raw.x = v.x;
        if (v.y != null) raw.y = v.y;
        if (kind === 'overlay') {
            if (v.w != null) raw.w = v.w;
            if (v.o != null) raw.opacity = Math.max(0.05, v.o);
        } else if (v.s != null) raw.sizePct = v.s;
    }
    function sortKeys(raw) { raw.keys.sort(function (a, b) { return a.t - b.t; }); }
    /**
     * Записать значения в момент t. Без ключей это просто новое место
     * элемента; когда движение уже есть — ключ в этом кадре (поправленный или
     * новый), как в монтажных программах. Меняет запись проекта на месте
     * («отменить» держит прежний снимок). Возвращает ключ или null.
     */
    function setValuesAt(kind, raw, t, vals) {
        if (!raw.keys || !raw.keys.length) { applyStatic(kind, raw, vals); return null; }
        var k = keyNear(raw, t);
        if (!k) {
            k = Object.assign({ t: t }, valuesAt(kind, raw, t));
            raw.keys.push(k);
            sortKeys(raw);
        }
        Object.keys(vals).forEach(function (f) { if (vals[f] != null) k[f] = vals[f]; });
        return k;
    }
    /**
     * Ключ в этом кадре: нет — ставится с тем, что видно сейчас; есть —
     * снимается. Снятый последний ключ оставляет элемент там, где он был в
     * этот момент. Возвращает true, если ключ теперь есть.
     */
    function toggleKeyAt(kind, raw, t) {
        var k = keyNear(raw, t);
        var now = valuesAt(kind, raw, t);
        if (k) {
            raw.keys = raw.keys.filter(function (x) { return x !== k; });
            if (!raw.keys.length) applyStatic(kind, raw, now);
            return false;
        }
        raw.keys = (raw.keys || []).concat([Object.assign({ t: t }, now)]);
        sortKeys(raw);
        return true;
    }

    /* ── кадр вывода ─────────────────────────────────────────────────────── */

    var ASPECTS = { '16:9': [16, 9], '9:16': [9, 16], '1:1': [1, 1], '4:5': [4, 5], '4:3': [4, 3] };

    /** Пропорция кадра проекта; 'auto' — как у первого клипа (после поворота). */
    function aspectOf(key, clipsRaw) {
        if (ASPECTS[key]) return ASPECTS[key][0] / ASPECTS[key][1];
        var list = (clipsRaw || []).map(normClip);
        for (var i = 0; i < list.length; i++) {
            var c = list[i];
            if (c.width > 0 && c.height > 0) {
                var sw = (c.rotation === 90 || c.rotation === 270);
                return sw ? c.height / c.width : c.width / c.height;
            }
        }
        return 16 / 9;
    }

    /**
     * Размер кадра вывода: пропорция + короткая сторона. «720p» — это
     * КОРОТКАЯ сторона: вертикальное 720p — 720×1280. Стороны чётные
     * (H.264 4:2:0 иначе не кодируется).
     */
    function outputSize(aspect, shortSide) {
        var s = Math.max(2, Math.round(num(shortSide, 1080)));
        var w, h;
        if (aspect >= 1) { h = s; w = s * aspect; } else { w = s; h = s / aspect; }
        return { w: Math.max(2, Math.round(w / 2) * 2), h: Math.max(2, Math.round(h / 2) * 2) };
    }

    return {
        build: build,
        move: move,
        splitAt: splitAt,
        anchorsOf: anchorsOf,
        reanchor: reanchor,
        gainAt: gainAt,
        aspectOf: aspectOf,
        outputSize: outputSize,
        isStill: isStill,
        reversedClip: reversedClip,
        forwardClip: forwardClip,
        valuesAt: valuesAt,
        keyNear: keyNear,
        setValuesAt: setValuesAt,
        toggleKeyAt: toggleKeyAt,
        KEY_SNAP: KEY_SNAP,
        TRANSITIONS: TRANSITIONS.slice(),
        FILTERS: Object.keys(FILTERS),
        ASPECTS: Object.keys(ASPECTS),
        clipOutDuration: function (raw) { return clipOutDuration(normClip(raw, 0)); },
        _normClip: normClip,
        _normText: normText,
        _normAudio: normAudio,
        _normOverlay: normOverlay,
    };
}));
